import './ContactList.scss';
import { Loading } from '@components';
import { Typography } from '@mui/material';
import { Api } from '@services';
import { find, map } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Typeahead } from 'react-bootstrap-typeahead';
import Select from 'react-select';
import Icon from '../../../../../components/Icon/Icon';

export const ContactList = () => {
  const [scheduleOpts, setScheduleOpts] = useState([]);
  const [deliverySchedule, setDeliverySchedule] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [notified, setNotified] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [busyByExternaId, setBusyByExternalId] = useState({});
  const typeaheadRef = useRef(null);

  const setBusy = (externalId, v) =>
    setBusyByExternalId((prev) =>
      v
        ? { ...prev, [externalId]: true }
        : ((prev) => {
            const copy = { ...prev };
            delete copy[externalId];
            return copy;
          })(prev),
    );

  const fetchSettings = useCallback(async () => {
    setIsLoading(true);

    const { data: contactsData } = await Api.get('/contacts');
    setContacts(contactsData);

    const { data: settings } = await Api.get('/reports/battery/settings');

    const deliverySchedules = map(settings.allDeliverySchedules, (schedule) => ({
      value: schedule.id,
      label: schedule.value,
    }));
    setScheduleOpts(deliverySchedules);

    const currentSchedule = find(deliverySchedules, { value: settings.deliveryScheduleId });
    setDeliverySchedule(currentSchedule);

    const _notified = map(settings.distributionList, (contact) => {
      return {
        contactId: contact.contactId,
        label: contact.email,
        externalId: contact.externalId,
      };
    });
    setNotified(_notified);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const onChangeDeliverySchedule = (schedule) => {
    setDeliverySchedule(schedule);
    updateSchedule(schedule.value);
  };

  const updateSchedule = async (schedule) => {
    try {
      setIsSaving(true);
      await Api.put('/reports/battery/settings', { deliveryScheduleId: schedule });
    } finally {
      await fetchSettings();
      setIsSaving(false);
    }
  };

  const addNotified = (contact) => {
    if (!contact) return;
    const externalId = contact.externalId;
    if (busyByExternaId[externalId] || notified.some((n) => n.externalId === externalId)) return;
    setIsLoading(true);
    setBusy(externalId, true);
    setNotified((prev) => [...prev, { ...contact, externalId }]);

    addContact(contact)
      .catch(() => {})
      .finally(async () => {
        await fetchSettings();
        setBusy(externalId, false);
      });
    setIsLoading(false);
  };

  const removeNotified = (contact) => {
    if (!contact) return;
    if (busyByExternaId[contact.externalId]) return;

    setBusy(contact.externalId, true);
    setNotified((prev) => prev.filter((n) => n.externalId !== contact.externalId));

    removeContact(contact)
      .catch(() => {})
      .finally(async () => {
        await fetchSettings();
        setBusy(contact.externalId, false);
      });
    setIsLoading(false);
  };

  const addContact = async (contact) => {
    try {
      await Api.post(`/reports/battery/contacts/${contact.externalId}`);
    } catch (e) {
      if (e?.response?.status !== 409) throw e;
    }
  };

  const removeContact = async (contact) => {
    try {
      await Api.delete(`/reports/battery/contacts/${contact.externalId}`);
    } catch (e) {
      if (e?.response?.status !== 409) throw e;
    }
  };
  return (
    <div className="contact-list">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <Typography>Delivery Schedule</Typography>
        <Select
          theme={(theme) => ({
            ...theme,
            colors: { ...theme.colors, primary: 'var(--c-primary)' },
          })}
          className="delivery-schedule"
          options={scheduleOpts}
          onChange={onChangeDeliverySchedule}
          value={deliverySchedule}
        />
      </div>

      <div className="mb-3">
        <Typeahead
          ref={typeaheadRef}
          id="contacts"
          placeholder="Search..."
          options={
            contacts &&
            contacts
              .filter((c) => c.emailAddress !== null)
              .map((c) => ({
                id: c.id,
                label: c.emailAddress || c.phoneNumber,
                externalId: c.externalId,
              }))
          }
          onChange={(selected) => {
            if (selected?.[0]) addNotified(selected[0]);
            typeaheadRef.current?.clear();
          }}
        />
      </div>
      {isLoading && <Loading width={200} height={200} />}
      {!isLoading && (
        <div>
          {notified.map((contact) => (
            <div
              key={contact.externalId}
              className="d-flex align-items-center justify-content-between mb-1"
            >
              <Typography>{contact.label}</Typography>
              <Icon
                name="add"
                variant="light"
                size="lg"
                disabled={!!busyByExternaId[contact.externalId]}
                onClick={(e) => {
                  setIsLoading(true);
                  e.preventDefault();
                  removeNotified(contact);
                }}
                showPointer={!busyByExternaId[contact.externalId]}
                className="sonarIcon-rotate-45 align-sub"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
